<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, ref, watch } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const emptyRuntime = {
  actions: { finance: {} },
  data: ref({}),
  error: ref(''),
  loading: ref(false),
  refresh: async () => null,
  saving: ref(false),
  selectedId: ref(''),
};

const realData = inject('imudeiRealData', null) || emptyRuntime;
const model = useImudeiRuntimeModel({
  realData,
  module: computed(() => 'finance'),
  screen: computed(() => 'detail'),
});

const selectedResponsibilityId = ref('');
const notice = ref('');

const asArray = value => (Array.isArray(value) ? value : []);
const valueAt = (value, path) =>
  path.split('.').reduce((current, key) => current?.[key], value);
const firstValue = (value, paths, fallback = '') => {
  const found = paths
    .map(path => valueAt(value, path))
    .find(item => item !== undefined && item !== null && item !== '');
  return found ?? fallback;
};
const displayText = value => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'object') {
    return firstValue(value, ['label', 'name', 'formatted', 'display'], '—');
  }
  return String(value);
};
const titleCase = value =>
  String(value || '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, letter => letter.toLocaleUpperCase('pt-BR'));
const initials = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const formatMoney = value => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'object') {
    const formatted = firstValue(value, ['formatted', 'display', 'label']);
    if (formatted) return formatted;
    return formatMoney(value.amount ?? value.value ?? value.amount_cents);
  }

  const amount = Number(value);
  if (Number.isNaN(amount)) return String(value);
  return new Intl.NumberFormat('pt-BR', {
    currency: 'BRL',
    style: 'currency',
  }).format(amount);
};
const formatDate = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeZone: 'UTC',
  }).format(parsed);
};
const formatDateTime = value => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: 'UTC',
  }).format(parsed);
};
const weekday = value => {
  if (!value) return 'Data indisponível';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return 'Data indisponível';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'UTC',
    weekday: 'long',
  }).format(parsed);
};
const eventTimestamp = value => {
  if (!value) return 0;
  const timestamp = new Date(value).valueOf();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const source = computed(
  () => model.detail.value?.source || realData.data.value?.detail || {}
);
const installment = computed(() => source.value.parcela || source.value);
const process = computed(
  () => source.value.dados_processo || source.value.processo || {}
);
const commission = computed(() => source.value.comissao_geral || {});
const currentStatus = computed(
  () => installment.value.status || source.value.status || {}
);
const statusLabel = computed(() =>
  displayText(
    firstValue(currentStatus.value, ['label', 'name', 'slug'], currentStatus.value)
  )
);
const statusTone = computed(() => {
  const status = statusLabel.value.toLocaleLowerCase('pt-BR');
  if (/pago|conclu|concili|confirm/.test(status)) {
    return 'bg-emerald-100 text-emerald-700';
  }
  if (/cancel|atras|erro/.test(status)) return 'bg-rose-100 text-rose-700';
  if (/pend|aguard|prev/.test(status)) return 'bg-orange-100 text-orange-700';
  return 'bg-violet-100 text-[#5a24c7]';
});
const recordId = computed(
  () => model.detail.value?.id || installment.value.id || source.value.id || ''
);
const recordCode = computed(() =>
  firstValue(process.value, ['codigo', 'reference'], recordId.value ? `#${recordId.value}` : '—')
);
const movementType = computed(() =>
  firstValue(source.value, [
    'tipo_lancamento',
    'movement_type',
    'entry_type',
    'kind',
  ])
);
const installmentNumber = computed(() =>
  firstValue(installment.value, ['numero_parcela', 'numero', 'sequence'])
);
const installmentTotal = computed(() =>
  firstValue(installment.value, ['total_parcelas', 'installments_total']) ||
  asArray(commission.value.parcelas).length
);
const recordTitle = computed(() => {
  const type = movementType.value ? titleCase(movementType.value) : '';
  const sequence = installmentNumber.value
    ? `Parcela ${installmentNumber.value}${
        installmentTotal.value ? ` de ${installmentTotal.value}` : ''
      }`
    : '';
  if (type && sequence) return `${type} — ${sequence}`;
  return (
    sequence ||
    type ||
    process.value.titulo ||
    model.detail.value?.title ||
    'Lançamento financeiro'
  );
});
const dueDate = computed(() =>
  firstValue(installment.value, ['data_prevista', 'due_at', 'due_date'])
);
const amount = computed(() =>
  formatMoney(firstValue(installment.value, ['valor_parcela', 'amount', 'value']))
);
const propertyAddress = computed(() => {
  const address = installment.value.endereco || source.value.endereco || {};
  return [
    address.logradouro,
    address.numero,
    address.unidade,
    address.bairro,
    address.cidade,
    address.uf,
  ]
    .filter(Boolean)
    .join(' · ');
});
const propertyTitle = computed(() =>
  firstValue(process.value, ['titulo', 'title'], propertyAddress.value || 'Imóvel indisponível')
);
const beneficiaryName = computed(() =>
  firstValue(source.value, [
    'beneficiary.name',
    'beneficiario.name',
    'payee.name',
    'dados_processo.loja.name',
    'loja.name',
  ])
);
const beneficiaryDetails = computed(() =>
  firstValue(source.value, [
    'beneficiary.document',
    'beneficiario.documento',
    'payee.document',
    'beneficiary.cnpj',
  ])
);
const financeActions = computed(
  () => source.value.acoes || source.value.actions || {}
);
const suggestedStatus = computed(() =>
  firstValue(source.value, ['sugestao_status', 'suggested_status'])
);
const canTransition = computed(
  () =>
    Boolean(
      suggestedStatus.value &&
        (financeActions.value.pode_alterar_status ||
          financeActions.value.can_transition ||
          source.value.permissions?.can_transition) &&
        typeof realData.actions.finance?.transition === 'function'
    )
);
const paymentResponsibilities = computed(() =>
  asArray(installment.value.responsaveis_pagamento).map(responsibility => ({
    canConfirm: Boolean(
      responsibility.acoes?.pode_confirmar ||
        responsibility.actions?.can_confirm ||
        responsibility.can_confirm
    ),
    confirmed: Boolean(
      responsibility.confirmacao?.confirmada ||
        responsibility.confirmacao_pagamento ||
        responsibility.comfirmacao_pagamento
    ),
    confirmedAt: firstValue(responsibility, [
      'confirmacao.em',
      'confirmed_at',
      'pagamento.data',
    ]),
    id: String(responsibility.id || responsibility.pagamento_id || ''),
    name: firstValue(responsibility, [
      'usuario_nome',
      'responsavel_nome',
      'property_transaction_party.name',
    ]),
    value: formatMoney(
      firstValue(responsibility, ['valor_pagamento', 'pagamento.valor'])
    ),
  }))
);
const pendingResponsibilities = computed(() =>
  paymentResponsibilities.value.filter(
    responsibility =>
      responsibility.id && !responsibility.confirmed && responsibility.canConfirm
  )
);
const canConfirmPayment = computed(
  () =>
    Boolean(
      recordId.value &&
        selectedResponsibilityId.value &&
        typeof realData.actions.finance?.confirmPayment === 'function'
    )
);

watch(
  pendingResponsibilities,
  responsibilities => {
    if (
      responsibilities.some(
        responsibility => responsibility.id === selectedResponsibilityId.value
      )
    ) {
      return;
    }
    selectedResponsibilityId.value = responsibilities[0]?.id || '';
  },
  { immediate: true }
);

const rawEvents = computed(() => {
  const detailEvents = asArray(
    firstValue(source.value, ['historico', 'history', 'events'])
  );
  return detailEvents.length ? detailEvents : model.history.value;
});
const events = computed(() =>
  rawEvents.value
    .map((event, index) => {
      const status = firstValue(event, ['status_novo', 'to_status']);
      const type = firstValue(event, ['label', 'tipo', 'event_type', 'action']);
      let label = 'Atualização registrada';
      if (status) {
        label = `Status atualizado para ${titleCase(status)}`;
      } else if (type) {
        label = titleCase(type);
      }

      return {
        actor: firstValue(event, ['ator.name', 'actor.name', 'user.name'], 'Sistema'),
        date: firstValue(event, [
          'ocorrido_em',
          'occurred_at',
          'created_at',
          'updated_at',
          'em',
          'at',
        ]),
        detail: firstValue(event, ['motivo', 'reason', 'notes']),
        id: String(event.id || event.sequencia || `${type}-${index}`),
        label,
      };
    })
    .sort((first, second) => eventTimestamp(first.date) - eventTimestamp(second.date))
);
const workflowSteps = computed(() => {
  if (events.value.length) return events.value.slice(-4);
  if (statusLabel.value !== '—') {
    return [
      {
        date: firstValue(installment.value, [
          'data_criacao_status',
          'status_changed_at',
        ]),
        id: 'current-status',
        label: statusLabel.value,
      },
    ];
  }
  return [];
});
const timelineItems = computed(() => {
  const items = events.value.map(event => ({
    ...event,
    kind: 'done',
    summary: event.actor,
  }));

  if (dueDate.value) {
    items.push({
      date: dueDate.value,
      id: 'due-date',
      kind: 'warning',
      label: 'Vencimento',
      summary: 'Data prevista',
    });
  }

  if (paymentResponsibilities.value.length) {
    const allConfirmed = paymentResponsibilities.value.every(
      responsibility => responsibility.confirmed
    );
    const confirmedAt = paymentResponsibilities.value.find(
      responsibility => responsibility.confirmedAt
    )?.confirmedAt;
    items.push({
      date: confirmedAt,
      id: 'payment',
      kind: allConfirmed ? 'done' : 'pending',
      label: allConfirmed ? 'Pagamento confirmado' : 'Pagamento',
      summary: allConfirmed
        ? 'Responsabilidades confirmadas'
        : 'Aguardando confirmação',
    });
  }

  return items
    .sort((first, second) => eventTimestamp(first.date) - eventTimestamp(second.date))
    .slice(-5);
});
const activityItems = computed(() => events.value.slice().reverse().slice(0, 5));
const details = computed(() => [
  {
    label: 'Tipo de lançamento',
    value: movementType.value ? titleCase(movementType.value) : 'Não informado',
  },
  {
    label: 'Forma de recebimento',
    value: displayText(
      firstValue(commission.value, ['forma_pagamento', 'payment_method'])
    ),
  },
  {
    accent: 'text-[#5a24c7]',
    label: 'Categoria',
    value: displayText(firstValue(commission.value, ['comissao', 'category'])),
  },
  {
    label: 'Conta bancária',
    value: displayText(
      firstValue(source.value, ['conta_bancaria.nome', 'bank_account.name'])
    ),
  },
  {
    label: 'Competência',
    value: formatDate(firstValue(source.value, ['data_competencia', 'competence_on'])),
  },
  {
    accent: statusTone.value.includes('orange') ? 'text-orange-500' : '',
    label: 'Conciliação',
    value: statusLabel.value,
  },
  {
    label: 'Centro de custo',
    value: displayText(firstValue(process.value, ['loja.name', 'store.name'])),
  },
  {
    label: 'Nosso número / Doc.',
    value: displayText(
      firstValue(source.value, ['nosso_numero', 'document_number'])
    ),
  },
  {
    label: 'Descrição',
    value: displayText(
      firstValue(source.value, [
        'descricao',
        'description',
        'comissao_geral.observacoes',
        'notes',
      ])
    ),
    wide: true,
  },
  {
    accent: 'text-[#5a24c7]',
    label: 'Tags',
    value:
      asArray(firstValue(source.value, ['tags'])).map(tag => displayText(tag)).join(' · ') ||
      '—',
    wide: true,
  },
]);
const splitRows = computed(() =>
  asArray(
    firstValue(source.value, ['usuarios_agrupado', 'usuarios_agrupados'])
  ).map((split, index) => ({
    id: String(split.usuario?.id || split.usuario_id || split.empresa_id || index),
    name: firstValue(split, ['usuario.name', 'nome', 'name'], 'Beneficiário não informado'),
    remaining: formatMoney(firstValue(split, ['valor_restante', 'valor_faltante'])),
    status: displayText(firstValue(split, ['status.slug', 'status.label', 'status'])),
    total: formatMoney(firstValue(split, ['valor_confirmado', 'valor_total'])),
    transferred: formatMoney(firstValue(split, ['valor_transferido'])),
    type: displayText(firstValue(split, ['tipo', 'usuario.tipo'])),
  }))
);
const rateio = computed(() => source.value.rateio || installment.value.rateio || {});
const rateioTotal = computed(() =>
  formatMoney(firstValue(rateio.value, ['valor_total', 'total_amount']))
);
const rateioTransferred = computed(() =>
  formatMoney(firstValue(rateio.value, ['valor_transferido', 'transferred_amount']))
);
const rateioRemaining = computed(() =>
  formatMoney(firstValue(rateio.value, ['valor_restante', 'remaining_amount']))
);
const documents = computed(() => {
  const boletoDocuments = asArray(installment.value.responsaveis_pagamento)
    .map(responsibility => ({
      date: firstValue(responsibility, ['boleto.enviado_em', 'data_envio']),
      id: `boleto-${responsibility.id || responsibility.pagamento_id}`,
      name: firstValue(responsibility, ['boleto.nome', 'nome_boleto']),
      type: 'Boleto',
    }))
    .filter(document => document.name);
  const sheet = installment.value.planilha || {};
  if (sheet.disponivel && sheet.filename) {
    boletoDocuments.push({
      date: installment.value.ultima_data_envio,
      id: `sheet-${sheet.id || sheet.filename}`,
      name: sheet.filename,
      type: 'Planilha',
    });
  }
  return boletoDocuments;
});
const gamification = computed(() =>
  firstValue(source.value, ['gamificacao', 'gamification', 'pontuacao', 'score'], {})
);
const gamificationAvailable = computed(
  () => Object.keys(gamification.value || {}).length > 0
);
const gamificationMission = computed(() =>
  firstValue(gamification.value, ['missao.titulo', 'mission.title', 'mission.label'])
);
const gamificationProgress = computed(() => {
  const progress = Number(
    firstValue(gamification.value, [
      'missao.progresso',
      'mission.progress',
      'progresso',
      'progress',
    ])
  );
  return Number.isFinite(progress) ? Math.min(Math.max(progress, 0), 100) : null;
});
const gamificationProgressClass = computed(() => {
  const progress = gamificationProgress.value;
  if (progress === null || progress === 0) return 'w-0';
  if (progress < 17) return 'w-1/6';
  if (progress < 34) return 'w-1/3';
  if (progress < 51) return 'w-1/2';
  if (progress < 67) return 'w-2/3';
  if (progress < 84) return 'w-5/6';
  return 'w-full';
});
const achievements = computed(() =>
  asArray(firstValue(gamification.value, ['conquistas', 'achievements']))
    .map((achievement, index) => ({
      description: firstValue(achievement, ['descricao', 'description']),
      id: String(achievement.id || achievement.key || index),
      title: firstValue(achievement, ['titulo', 'title', 'name']),
    }))
    .filter(achievement => achievement.title)
    .slice(0, 4)
);
const error = computed(() => realData.error?.value || model.localError.value || '');
const loading = computed(() => Boolean(realData.loading?.value));
const saving = computed(() => Boolean(realData.saving?.value));

const applySuggestedStatus = async () => {
  if (!canTransition.value || !recordId.value) return;

  notice.value = '';
  try {
    await realData.actions.finance.transition(recordId.value, {
      target_status: suggestedStatus.value,
    });
    notice.value = 'Status atualizado com a resposta da API financeira.';
  } catch {
    notice.value = '';
  }
};
const confirmPayment = async () => {
  if (!canConfirmPayment.value) return;

  notice.value = '';
  try {
    await realData.actions.finance.confirmPayment(
      recordId.value,
      selectedResponsibilityId.value,
      { confirmed: true }
    );
    notice.value = 'Pagamento confirmado com a resposta da API financeira.';
  } catch {
    notice.value = '';
  }
};
</script>

<template>
  <main
    data-imudei-visual-id="finance-detail-root"
    class="mx-auto w-full max-w-[1325px] px-4 pb-8 pt-3 text-[#101958]"
  >
    <header
      data-imudei-visual-id="finance-detail-header"
      class="flex min-h-[62px] items-start justify-between gap-6"
    >
      <div class="min-w-0">
        <p class="text-[10px] text-[#5a32bd]">
          Financeiro <span class="mx-2 text-[#a5adc1]">/</span> {{ recordCode }}
        </p>
        <div class="mt-2 flex items-center gap-3">
          <h1 class="truncate text-[25px] font-bold">{{ recordTitle }}</h1>
          <span
            class="shrink-0 rounded-full px-3 py-1 text-[8px] font-semibold"
            :class="statusTone"
          >
            {{ statusLabel }}
          </span>
        </div>
      </div>

      <div class="flex shrink-0 flex-wrap justify-end gap-3">
        <button
          v-if="canTransition"
          class="h-9 rounded-lg border border-[#9f79ee] px-4 text-[9px] font-semibold text-[#5a24c7] disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          :disabled="saving"
          @click="applySuggestedStatus"
        >
          <i class="i-lucide-refresh-cw mr-2 inline size-3.5" />
          {{ saving ? 'Processando…' : `Atualizar para ${titleCase(suggestedStatus)}` }}
        </button>
        <button
          v-if="pendingResponsibilities.length"
          class="h-9 rounded-lg bg-gradient-to-r from-[#4d0db8] to-[#6f16cd] px-4 text-[9px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
          type="button"
          :disabled="saving || !canConfirmPayment"
          @click="confirmPayment"
        >
          <i class="i-lucide-plus mr-2 inline size-3.5" />
          {{ saving ? 'Processando…' : 'Registrar pagamento' }}
        </button>
      </div>
    </header>

    <p
      v-if="loading || error || notice"
      class="mt-3 rounded-xl border px-3 py-2 text-[8px]"
      :class="
        error
          ? 'border-rose-100 bg-rose-50 text-rose-600'
          : notice
            ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
            : 'border-violet-100 bg-violet-50 text-[#5a24c7]'
      "
    >
      {{ error || notice || 'Carregando dados financeiros retornados pela API…' }}
    </p>

    <section class="mt-4 grid grid-cols-[minmax(0,1fr)_180px] gap-3">
      <section class="min-w-0 space-y-3">
        <article
          data-imudei-visual-id="finance-detail-hero"
          class="grid grid-cols-[1fr_.85fr_1.2fr_1.1fr_1.25fr] rounded-2xl border border-[#e8e4ef] bg-white p-4"
        >
          <span>
            <small class="text-[8px] text-[#66739b]">Valor previsto</small>
            <strong class="mt-2 block text-[20px] text-emerald-500">{{ amount }}</strong>
          </span>
          <span class="border-l border-[#eeeaf4] pl-4">
            <small class="text-[8px] text-[#66739b]">Vencimento</small>
            <strong class="mt-2 block text-[11px]">
              <i class="i-lucide-calendar-days mr-2 inline size-4 text-[#5a24c7]" />
              {{ formatDate(dueDate) }}
            </strong>
            <small class="mt-1 block text-[8px] capitalize">{{ weekday(dueDate) }}</small>
          </span>
          <span class="border-l border-[#eeeaf4] pl-4">
            <small class="text-[8px] text-[#66739b]">Beneficiário</small>
            <span class="mt-2 flex items-center gap-2">
              <i class="grid size-8 place-items-center rounded-full bg-[#4315a8] text-[9px] font-bold text-white">
                {{ initials(beneficiaryName) }}
              </i>
              <b class="truncate text-[9px]">{{ beneficiaryName || 'Não informado' }}</b>
            </span>
            <small class="ml-10 block truncate text-[7px] text-[#66739b]">
              {{ beneficiaryDetails || 'Dados não informados' }}
            </small>
          </span>
          <span class="border-l border-[#eeeaf4] pl-4">
            <small class="text-[8px] text-[#66739b]">Venda vinculada</small>
            <strong class="mt-2 block truncate text-[10px] text-[#5a24c7]">{{ recordCode }}</strong>
            <small class="mt-1 block truncate text-[8px]">{{ process.titulo || 'Não informada' }}</small>
          </span>
          <span class="border-l border-[#eeeaf4] pl-4">
            <small class="text-[8px] text-[#66739b]">Imóvel</small>
            <span class="mt-2 flex gap-2">
              <i class="grid size-10 shrink-0 place-items-center rounded-lg bg-violet-100 text-[#5a24c7]">
                <i class="i-lucide-building-2 size-5" />
              </i>
              <span class="min-w-0">
                <strong class="block truncate text-[9px]">{{ propertyTitle }}</strong>
                <small class="block truncate text-[7px] text-[#66739b]">
                  {{ propertyAddress || 'Endereço indisponível' }}
                </small>
              </span>
            </span>
          </span>
        </article>

        <article
          data-imudei-visual-id="finance-detail-workflow"
          class="flex min-h-[66px] items-center rounded-2xl border border-[#e8e4ef] bg-white px-6 py-3"
        >
          <p v-if="!workflowSteps.length" class="text-[9px] text-[#66739b]">
            Histórico de status indisponível para esta parcela.
          </p>
          <template v-else>
            <span
              v-for="(step, index) in workflowSteps"
              :key="step.id"
              class="relative flex flex-1 items-center gap-3"
            >
              <i class="z-10 grid size-8 place-items-center rounded-full border-2 border-[#5421c7] bg-white text-[#5421c7]">
                <span class="i-lucide-check size-4" />
              </i>
              <span class="min-w-0">
                <strong class="block truncate text-[9px]">{{ step.label }}</strong>
                <small class="block text-[8px] text-[#66739b]">{{ formatDate(step.date) }}</small>
              </span>
              <i
                v-if="index < workflowSteps.length - 1"
                class="mx-4 h-px flex-1 border-t border-dashed border-[#cbbdff]"
              />
            </span>
          </template>
        </article>

        <nav data-imudei-visual-id="finance-detail-tabs" class="flex gap-7 border-b border-[#eeeaf4] px-3">
          <span class="border-b-2 border-[#5a24c7] px-1 py-3 text-[9px] font-bold text-[#5a24c7]">Resumo</span>
          <span class="border-b-2 border-transparent px-1 py-3 text-[9px] text-[#66739b]">Parcelas</span>
          <span class="border-b-2 border-transparent px-1 py-3 text-[9px] text-[#66739b]">Rateio</span>
          <span class="border-b-2 border-transparent px-1 py-3 text-[9px] text-[#66739b]">Documentos</span>
          <span class="border-b-2 border-transparent px-1 py-3 text-[9px] text-[#66739b]">Histórico</span>
        </nav>

        <section data-imudei-visual-id="finance-detail-content" class="grid grid-cols-[1.35fr_.65fr_.85fr] gap-3">
          <article
            data-imudei-visual-id="finance-detail-fields"
            class="rounded-2xl border border-[#e8e4ef] bg-white p-4"
          >
            <h2 class="text-[11px] font-bold">Detalhes do lançamento</h2>
            <div class="mt-4 grid grid-cols-2 gap-x-8 gap-y-4 text-[9px]">
              <span
                v-for="item in details"
                :key="item.label"
                :class="item.wide ? 'col-span-2' : ''"
              >
                <small class="block text-[#66739b]">{{ item.label }}</small>
                <strong class="mt-1 block break-words" :class="item.accent">{{ item.value }}</strong>
              </span>
            </div>

            <div
              v-if="pendingResponsibilities.length"
              class="mt-5 rounded-xl border border-violet-100 bg-violet-50 p-3"
            >
              <label class="block text-[8px] font-semibold text-[#372264]">
                Responsabilidade de pagamento
                <select
                  v-model="selectedResponsibilityId"
                  class="mt-1.5 h-8 w-full rounded-lg border border-[#ded9e9] bg-white px-2 text-[9px] font-normal text-[#101958] outline-none focus:border-[#805ce1]"
                >
                  <option
                    v-for="responsibility in pendingResponsibilities"
                    :key="responsibility.id"
                    :value="responsibility.id"
                  >
                    {{ responsibility.name || 'Responsável não informado' }} · {{ responsibility.value }}
                  </option>
                </select>
              </label>
            </div>
          </article>

          <article
            data-imudei-visual-id="finance-detail-timeline"
            class="rounded-2xl border border-[#e8e4ef] bg-white p-4"
          >
            <h2 class="text-[11px] font-bold">Linha do tempo da parcela</h2>
            <p v-if="!timelineItems.length" class="mt-4 text-[8px] text-[#66739b]">
              Nenhum evento retornado para esta parcela.
            </p>
            <div v-else class="mt-4 space-y-4">
              <div v-for="item in timelineItems" :key="item.id" class="flex gap-3">
                <i
                  class="grid size-6 shrink-0 place-items-center rounded-full border"
                  :class="
                    item.kind === 'warning'
                      ? 'border-orange-300 bg-orange-50 text-orange-500'
                      : item.kind === 'pending'
                        ? 'border-[#d8d4e3] bg-white text-[#8b92a9]'
                        : 'border-emerald-400 bg-emerald-50 text-emerald-600'
                  "
                >
                  <span
                    :class="
                      item.kind === 'warning'
                        ? 'i-lucide-calendar-clock'
                        : item.kind === 'pending'
                          ? 'i-lucide-clock-3'
                          : 'i-lucide-check'
                    "
                    class="size-3.5"
                  />
                </i>
                <span class="min-w-0">
                  <strong class="block text-[8px]">{{ item.label }}</strong>
                  <small class="mt-1 block text-[7px] text-[#66739b]">{{ formatDateTime(item.date) }}</small>
                  <small v-if="item.summary" class="mt-1 block text-[7px] text-[#5a24c7]">{{ item.summary }}</small>
                </span>
              </div>
            </div>
          </article>

          <section class="space-y-3">
            <article
              data-imudei-visual-id="finance-detail-activities"
              class="rounded-2xl border border-[#e8e4ef] bg-white p-4"
            >
              <h2 class="text-[11px] font-bold">Atividades</h2>
              <p v-if="!activityItems.length" class="mt-4 text-[8px] text-[#66739b]">
                Nenhuma atividade retornada pela API.
              </p>
              <div v-else>
                <div v-for="item in activityItems" :key="item.id" class="mt-3 flex gap-2">
                  <i class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]">
                    {{ initials(item.actor) }}
                  </i>
                  <span class="min-w-0 flex-1">
                    <strong class="block truncate text-[8px]">{{ item.label }}</strong>
                    <small class="block truncate text-[7px] text-[#66739b]">{{ item.actor }}</small>
                  </span>
                  <small class="shrink-0 text-[7px] text-[#66739b]">{{ formatDateTime(item.date) }}</small>
                </div>
              </div>
            </article>

            <article
              data-imudei-visual-id="finance-detail-documents"
              class="rounded-2xl border border-[#e8e4ef] bg-white p-4"
            >
              <h2 class="text-[10px] font-bold">Documentos anexos</h2>
              <p v-if="!documents.length" class="mt-3 text-[8px] text-[#66739b]">
                Nenhum documento disponível para esta parcela.
              </p>
              <div v-else>
                <div v-for="document in documents" :key="document.id" class="mt-3 flex items-center gap-2 rounded-lg border border-dashed border-[#d8cfed] px-2 py-2">
                  <i class="i-lucide-file-text size-4 text-[#5a24c7]" />
                  <span class="min-w-0 flex-1">
                    <strong class="block truncate text-[8px]">{{ document.name }}</strong>
                    <small class="block text-[7px] text-[#66739b]">{{ document.type }} · {{ formatDate(document.date) }}</small>
                  </span>
                </div>
              </div>
            </article>

            <article
              data-imudei-visual-id="finance-detail-audit"
              class="rounded-2xl border border-[#e8e4ef] bg-white p-4"
            >
              <h2 class="text-[10px] font-bold">Trilha de auditoria</h2>
              <p v-if="!events.length" class="mt-3 text-[8px] text-[#66739b]">
                A API não retornou eventos auditáveis.
              </p>
              <div v-else>
                <div v-for="item in events.slice().reverse().slice(0, 4)" :key="item.id" class="mt-3 flex gap-2">
                  <i class="mt-1 size-2 shrink-0 rounded-full bg-emerald-500" />
                  <small class="text-[8px] text-[#66739b]">{{ formatDateTime(item.date) }} · {{ item.label }}</small>
                </div>
              </div>
            </article>
          </section>
        </section>
        <article
          data-imudei-visual-id="finance-detail-footer"
          class="rounded-2xl border border-[#e8e4ef] bg-white p-4"
        >
          <h2 class="text-[11px] font-bold">Rateio de comissões vinculadas</h2>
          <div class="mt-3 grid grid-cols-[1.3fr_.55fr_.7fr_.7fr_.7fr] gap-3 border-b border-[#eeeaf4] pb-2 text-[7px] text-[#66739b]">
            <span>Beneficiário</span>
            <span>Tipo</span>
            <span>Confirmado</span>
            <span>Transferido</span>
            <span>Restante</span>
          </div>
          <p v-if="!splitRows.length" class="py-4 text-[8px] text-[#66739b]">
            Nenhum rateio retornado para esta parcela.
          </p>
          <template v-else>
            <div
              v-for="split in splitRows"
              :key="split.id"
              class="mt-2 grid grid-cols-[1.3fr_.55fr_.7fr_.7fr_.7fr] items-center gap-3 text-[8px]"
            >
              <span class="flex min-w-0 items-center gap-2">
                <i class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]">
                  {{ initials(split.name) }}
                </i>
                <span class="min-w-0">
                  <strong class="block truncate">{{ split.name }}</strong>
                  <small class="block text-[7px] text-[#66739b]">{{ split.status }}</small>
                </span>
              </span>
              <span>{{ split.type }}</span>
              <span>{{ split.total }}</span>
              <span>{{ split.transferred }}</span>
              <span>{{ split.remaining }}</span>
            </div>
          </template>
          <div class="mt-3 grid grid-cols-[1.3fr_.55fr_.7fr_.7fr_.7fr] gap-3 border-t border-[#eeeaf4] pt-3 text-[8px] font-bold">
            <span>Total</span>
            <span />
            <span>{{ rateioTotal }}</span>
            <span>{{ rateioTransferred }}</span>
            <span>{{ rateioRemaining }}</span>
          </div>
        </article>
      </section>

      <aside data-imudei-visual-id="finance-detail-sidebar" class="space-y-3">
        <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <header class="flex items-center justify-between">
            <h2 class="text-[10px] font-bold">Missão diária</h2>
            <i class="i-lucide-target size-4 text-[#5a24c7]" />
          </header>
          <template v-if="gamificationAvailable && gamificationMission">
            <p class="mt-4 text-[9px]">{{ gamificationMission }}</p>
            <div class="mt-3 h-1.5 rounded-full bg-violet-100">
              <i class="block h-full rounded-full bg-[#5a24c7]" :class="gamificationProgressClass" />
            </div>
            <small class="mt-2 block text-[8px] text-[#66739b]">
              {{ gamificationProgress === null ? 'Progresso não informado' : `${gamificationProgress}% concluído` }}
            </small>
          </template>
          <p v-else class="mt-4 text-[8px] leading-4 text-[#66739b]">
            Gamificação indisponível para este lançamento.
          </p>
        </article>

        <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[10px] font-bold">Conquistas</h2>
          <p v-if="!achievements.length" class="mt-4 text-[8px] leading-4 text-[#66739b]">
            Nenhuma conquista retornada para este lançamento.
          </p>
          <div v-else>
            <div v-for="achievement in achievements" :key="achievement.id" class="mt-4 flex gap-2">
              <i class="grid size-8 shrink-0 place-items-center rounded-lg bg-violet-50 text-[#5a24c7]">
                <span class="i-lucide-award size-4" />
              </i>
              <span class="min-w-0">
                <strong class="block text-[8px]">{{ achievement.title }}</strong>
                <small v-if="achievement.description" class="block text-[7px] text-[#66739b]">{{ achievement.description }}</small>
              </span>
            </div>
          </div>
        </article>

        <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[10px] font-bold">Feed de atividades</h2>
          <p v-if="!activityItems.length" class="mt-3 text-[8px] leading-4 text-[#66739b]">
            Nenhuma atividade retornada para esta parcela.
          </p>
          <div v-else>
            <div v-for="item in activityItems.slice(0, 3)" :key="item.id" class="mt-4 flex gap-2">
              <i class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[7px] font-bold text-[#5a24c7]">
                {{ initials(item.actor) }}
              </i>
              <span class="min-w-0">
                <strong class="block truncate text-[8px]">{{ item.actor }}</strong>
                <small class="block text-[7px] text-[#66739b]">{{ item.label }}</small>
              </span>
            </div>
          </div>
        </article>

        <article class="rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <h2 class="text-[10px] font-bold">Selo de confiabilidade</h2>
          <div class="mt-3 flex items-center gap-3">
            <i class="grid size-11 shrink-0 place-items-center rounded-2xl bg-violet-100 text-[#5a24c7]">
              <span class="i-lucide-shield-check size-6" />
            </i>
            <span>
              <strong class="block text-[10px]">{{ events.length ? 'Trilha disponível' : 'Trilha indisponível' }}</strong>
              <small class="block text-[7px] leading-3 text-[#66739b]">
                {{ events.length ? 'Eventos retornados pela API financeira.' : 'Aguardando eventos auditáveis.' }}
              </small>
            </span>
          </div>
        </article>
      </aside>
    </section>
  </main>
</template>
